/**
 * Genesys Web Messaging v3 helpers
 *
 * In v3, member validation data is passed via customAttributes in the Database.set
 * command (or customAttributes in the messaging payload), not the old "Custom Variables."
 *
 * Configure in Genesys Cloud Admin:
 * - Web Messaging Deployment (generates Deployment ID)
 * - Messenger Configuration (bot/agent behavior)
 * - Allowed Domains (whitelist your domain or localhost)
 * - HEADLESS MODE: Enable for custom UI - the Genesys widget will NOT appear,
 *   only our WebChat component is shown. Required for useGenesysMessaging.
 */

/** Platform domain -> WebSocket region (webmessaging.<region>.pure.cloud) */
const PLATFORM_TO_WS_REGION = {
  'mypurecloud.ie': 'euw1',
  'mypurecloud.com': 'useast1',
  'mypurecloud.de': 'euc1',
  'mypurecloud.jp': 'apne1',
  'mypurecloud.com.au': 'aps1',
};

/**
 * Get WebSocket host for Genesys Web Messaging.
 * URL format: wss://webmessaging.<region>.pure.cloud/v1/app/<deploymentId>
 *
 * @param {string} region - Platform domain (mypurecloud.ie) or region code (euw1, usw2)
 * @returns {string} Host for WebSocket URL
 */
export function getGenesysWebMessagingHost(region = '') {
  const r = (region || 'mypurecloud.ie').toLowerCase();
  const wsRegion = PLATFORM_TO_WS_REGION[r] || r;
  return `webmessaging.${wsRegion}.pure.cloud`;
}

/**
 * Build customAttributes for Genesys Web Messaging v3.
 * Passes member validation data from form context when available.
 * 
 * IMPORTANT: Property names are case-sensitive and must match exactly
 * what is configured in Genesys Cloud Admin custom attributes.
 *
 * @param {Object} userInfo - User and practice details captured in form context
 * @returns {Object} customAttributes for Database.set
 */
export function getGenesysCustomAttributes(userInfo = {}) {
  const medicalAidNumber = userInfo.MedicalAidNumber || userInfo.medicalAidNumber || '';
  const patientEmail = userInfo.PatientEmail || userInfo.email || '';
  const patientCellNumber = userInfo.PatientCellNumber || userInfo.cellNumber || '';

  return {
    'Medical Aid Number': String(medicalAidNumber),
    MedicalAid: String(userInfo.MedicalAid || userInfo.Medicaloption || ''),
    PatientLastName: String(userInfo.PatientLastName || ''),
    PatientFirstName: String(userInfo.PatientFirstName || ''),
    PatientEmail: String(patientEmail),
    PatientDOB: String(userInfo.PatientDOB || ''),
    PatientCellNumber: String(patientCellNumber),
    PracticeName: String(userInfo.PracticeName || userInfo.PracticeDetails || ''),
    PracticeEmailAddress: String(userInfo.PracticeEmailAddress || ''),
    'Practice Number': String(userInfo['Practice Number'] || userInfo.PracticeNumber || ''),
    PracticeTelephone: String(userInfo.PracticeTelephone || ''),
  };
}

const OPEN_CHAT_EVENT = 'openPPNWebChat';

/**
 * Trigger hand-off to Genesys after saving form locally.
 * Call after saveToLocalDB(formData). Opens chat and sends message with validation fields.
 *
 * @param {string} initialMessage - e.g. 'Hi - Member Validation Submitted'
 * @param {Object} customAttributes - form fields for member validation (MedicalAidNumber, PatientDOB, PracticeName, etc.)
 *
 * @example
 * const onValidationSubmit = async (formData) => {
 *   await saveToLocalDB(formData);
 *   openChatWithHandOff('Hi - Member Validation Submitted', {
 *     MedicalAidNumber: formData.MedicalAidNumber,
 *     PatientDOB: formData.PatientDOB,
 *     PracticeName: formData.PracticeName,
 *     Email: formData.Email,
 *     CellNumber: formData.CellNumber,
 *     // ... all 11 validation fields
 *   });
 * };
 */
export function openChatWithHandOff(initialMessage = 'Hi', customAttributes = {}) {
  if (typeof window === 'undefined') return;

  // Always use the custom WebChat widget (headless WebSocket mode)
  window.dispatchEvent(
    new CustomEvent(OPEN_CHAT_EVENT, {
      detail: { initialMessage, customAttributes },
    })
  );
}

/**
 * Helper function to open chat with form data from member validation
 * Formats the data nicely for Genesys agents
 * 
 * @param {Object} formData - Form data object with all fields
 * 
 * @example
 * openChatWithFormData({
 *   FormType: 'Member Validation',
 *   MedicalAidNumber: '12345',
 *   PatientName: 'John',
 *   PatientSurname: 'Doe',
 *   Email: 'john@example.com',
 *   CellNumber: '+27123456789',
 *   // ... other fields
 * });
 */
export function openChatWithFormData(formData) {
  const formType = formData.FormType || 'Member Validation';
  const message = `${formType} Form Submitted`;

  // All form data will be sent as custom attributes
  // Agents will see this in their Genesys workspace
  openChatWithHandOff(message, formData);
}
